import { AlertTriangle, RotateCcw, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import type { RegressionResult } from '@/lib/artifactRegressionCheck';

export interface RegressionWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: RegressionResult | null;
  onApplyAnyway: () => void;
  onRetry: () => void;
  onCancel: () => void;
}

/**
 * Visas när AI-extend har tappat entiteter. User väljer:
 *  - Fortsätt ändå (apply trots regression)
 *  - Försök igen (be AI producera ny version)
 *  - Avbryt (discard)
 */
export function RegressionWarningDialog({
  open, onOpenChange, result, onApplyAnyway, onRetry, onCancel,
}: RegressionWarningDialogProps) {
  if (!result) return null;

  const { missingBlocks, renamedBlocks, removedConnectors, removedGroups } = result;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Regression upptäckt
          </DialogTitle>
          <DialogDescription>
            AI:n tog bort entiteter som fanns i föregående version. För en <strong>extend</strong>-operation
            förväntas vi bevara allt och bara lägga till nytt.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">{result.summary}</div>

          {missingBlocks.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-1">Blocks som saknas:</div>
              <ul className="text-xs space-y-0.5 max-h-32 overflow-y-auto border rounded p-2 bg-muted/40">
                {missingBlocks.map((b) => (
                  <li key={b.id}>• <strong>{b.name}</strong> <span className="text-muted-foreground">({b.type})</span></li>
                ))}
              </ul>
            </div>
          )}

          {renamedBlocks.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-1">Blocks som döpts om:</div>
              <ul className="text-xs space-y-0.5 max-h-24 overflow-y-auto border rounded p-2 bg-muted/40">
                {renamedBlocks.map((b) => (
                  <li key={b.id}>
                    <span className="text-muted-foreground">{b.oldName}</span> → <strong>{b.newName}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(removedConnectors > 0 || removedGroups > 0) && (
            <div className="text-xs">
              {removedConnectors > 0 && <div>• {removedConnectors} connector(s) borttagna</div>}
              {removedGroups > 0 && <div>• {removedGroups} group(s) borttagna</div>}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-2 flex-wrap">
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <X className="w-3.5 h-3.5 mr-1" /> Avbryt
            </Button>
            <Button variant="outline" size="sm" onClick={onRetry} data-testid="regression-revert-to-v2">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Återgå till v2
            </Button>
            <Button size="sm" onClick={onApplyAnyway}>
              <Check className="w-3.5 h-3.5 mr-1" /> Fortsätt ändå
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
